import { forwardRef, MouseEventHandler } from 'react';
import styles from './multi-select.module.css';
import { cn } from '../../../../utils';
import { FieldWrapper } from '../../../form';
import { Icon } from '../../../symbols';
import { BaseOption, MultiSelectProps } from './types';
import { useMultiSelect } from './multi-select.hook';
import { defaultRenderOption } from './option';
import { defaultRenderSelected } from './selected-item';

function MultiSelectInner<Option extends BaseOption>(
  {
    invert = false,
    options,
    renderOption = defaultRenderOption,
    renderSelected = defaultRenderSelected,
    defaultValue,
    separator = ';',
    label,
    keepOpen = true,
    value,
    fieldWrapperClassName,
    onChange,
    error,
    ...props
  }: MultiSelectProps<Option>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const {
    getLabelProps,
    selectedItems,
    getItemProps,
    getSelectedItemProps,
    getToggleButtonProps,
    getDropdownProps,
    getMenuProps,
    removeSelectedItem,
    highlightedIndex,
    isOpen,
    items,
  } = useMultiSelect<Option>({
    ...props,
    value,
    onChange,
    options,
    separator,
    defaultValue,
    keepOpen,
  });

  const removeItem =
    (item: string): MouseEventHandler<HTMLButtonElement> =>
    (e) => {
      e.stopPropagation();
      removeSelectedItem(item);
    };

  return (
    <FieldWrapper
      {...getLabelProps()}
      label={label}
      error={error}
      helpText={props.helpText}
      className={fieldWrapperClassName}
    >
      <div>
        <div ref={ref} className={cn(styles.wrapper, invert && styles.invert)}>
          <ul className={styles['selected-wrapper']} role="list">
            {selectedItems.map((selectedItem) => {
              const selectedOption = options.find((i) => i.id === selectedItem);
              if (!selectedOption) return null;

              const selectedItemProps = getSelectedItemProps({ selectedItem });

              return (
                <li key={selectedItem} role="listitem">
                  {renderSelected({
                    ...selectedOption,
                    ...selectedItemProps,
                    onRemove: removeItem(selectedItem),
                    highlighted: false,
                  })}
                </li>
              );
            })}
          </ul>
          <button
            {...getToggleButtonProps(
              getDropdownProps({ preventKeyAction: isOpen }),
            )}
            className={styles.button}
            type="button"
          >
            <Icon name="dropdown" />
          </button>
        </div>
        <menu {...getMenuProps()} className={styles.menu}>
          {isOpen &&
            items.map((item, index) => {
              const { ref: itemRef, ...itemProps } = getItemProps({
                item,
                index,
              });

              return (
                <li key={item.id}>
                  {renderOption({
                    ...item,
                    itemProps,
                    highlighted: highlightedIndex === index,
                    ref: itemRef,
                  })}
                </li>
              );
            })}
        </menu>
      </div>
    </FieldWrapper>
  );
}

export const MultiSelect = forwardRef(MultiSelectInner) as <
  Option extends BaseOption,
>(
  props: MultiSelectProps<Option> & { ref?: React.Ref<HTMLDivElement> },
) => ReturnType<typeof MultiSelectInner>;
